import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Select,
    MenuItem,
    Button,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow
} from '@mui/material';
import { api } from '../../services/api';

interface User {
    id: number;
    username: string;
}

interface Creditor {
    id: number;
    user_id: number;
    username: string;
    is_active: boolean;
    created_at: string;
}

const MarginCreditorsPage: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [creditors, setCreditors] = useState<Creditor[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<number | ''>('');

    const loadUsers = async () => {
        try {
            const response = await api.get('/api/v1/admin-panel/users-kyc', {
                params: {
                    page: 1,
                    limit: 100  // Get all users at once for the dropdown
                }
            });
            // Map the response to match the expected format
            const users = response.data.data.items.map(user => ({
                id: user.userId,  // Changed from user.id to user.userId
                username: user.username
            }));
            setUsers(users);
        } catch (err: any) {
            setError('Failed to load users');
        }
    };

    const loadCreditors = async () => {
        try {
            const response = await api.get('/api/v1/admin-panel/creditors');
            setCreditors(response.data.data);
        } catch (err: any) {
            setError('Failed to load creditors');
        }
    };

    const handleAddCreditor = async () => {
        try {
            setLoading(true);
            setError(null);
            await api.post('/api/v1/admin-panel/creditors', { userId: selectedUserId });
            await loadCreditors();
            setSelectedUserId('');
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to add creditor');
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveCreditor = async (id: number) => {
        try {
            setLoading(true);
            setError(null);
            await api.delete(`/api/v1/admin-panel/creditors/${id}`);
            await loadCreditors();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to remove creditor');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
        loadCreditors();
    }, []);

    return (
        <Box>
            <Typography variant="h4" gutterBottom>
                Управление кредиторами
            </Typography>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Добавить кредитора
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Select
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value as number)}
                        sx={{ minWidth: 200 }}
                        disabled={loading}
                    >
                        <MenuItem value="">
                            <em>Выберите пользователя</em>
                        </MenuItem>
                        {users.map(user => (
                            <MenuItem key={user.id} value={user.id}>
                                {user.username}
                            </MenuItem>
                        ))}
                    </Select>
                    <Button
                        variant="contained"
                        onClick={handleAddCreditor}
                        disabled={loading || !selectedUserId}
                    >
                        Добавить
                    </Button>
                </Box>
            </Paper>

            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Список кредиторов
                </Typography>

                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>ID</TableCell>
                            <TableCell>Пользователь</TableCell>
                            <TableCell>Статус</TableCell>
                            <TableCell>Дата добавления</TableCell>
                            <TableCell>Действия</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {creditors.map(creditor => (
                            <TableRow key={creditor.id}>
                                <TableCell>{creditor.id}</TableCell>
                                <TableCell>{creditor.username}</TableCell>
                                <TableCell>{creditor.is_active ? 'Активный' : 'Неактивный'}</TableCell>
                                <TableCell>{new Date(creditor.created_at).toLocaleString()}</TableCell>
                                <TableCell>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={() => handleRemoveCreditor(creditor.id)}
                                        disabled={loading || !creditor.is_active}
                                    >
                                        Удалить
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
};

export default MarginCreditorsPage;
